import { useNavigate, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Globe, Zap, TrendingUp, Users, MessageSquare, ChevronLeft, ChevronRight,
  Shield, Lock, CheckCircle, Package, Bot, Target, Clock, ArrowRight, 
  Sparkles, BadgeCheck, Mail, Phone, HelpCircle, DollarSign, FileText,
  RefreshCw, BookOpen, Calendar, Rocket
} from 'lucide-react';
import { toast } from 'sonner';
import LanguageSelector from '@/components/LanguageSelector';
import { useLanguage } from '@/context/LanguageContext';
import SEO, { SEO_CONFIGS } from '@/components/SEO';

const API = window.location.origin;

// Waitlist CTA Component (replacing fake testimonials)
const WaitlistSection = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setIsSubmitting(true);
    // Simulate submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success('You\'re on the waitlist! We\'ll notify you when ready.');
    }, 1000);
  };

  return (
    <section className="py-20 bg-white" data-testid="waitlist-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full mb-6">
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-medium">Early Access Open</span>
          </div>
          <h2 className="font-heading text-3xl font-bold text-primary mb-4">
            Be Among the First Exporters to Try GlobalVyapar
          </h2>
          <p className="text-muted-foreground mb-8">
            Join our early access program. Get free access during beta and help shape the future of export lead management.
          </p>
          
          {isSubmitted ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-3" />
              <p className="font-semibold text-green-900">You're on the list!</p>
              <p className="text-sm text-green-700 mt-1">We'll email you when your account is ready.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
                data-testid="waitlist-email"
              />
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="h-12 px-6 bg-primary"
                data-testid="waitlist-submit"
              >
                {isSubmitting ? 'Joining...' : 'Join Waitlist'}
              </Button>
            </form>
          )}
          
          <p className="text-xs text-muted-foreground mt-4">
            Free during beta. No credit card required.
          </p>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => (
  <section className="py-20 bg-slate-50" data-testid="pricing-section">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-12">
        <h2 className="font-heading text-3xl font-bold text-primary mb-4">
          Simple, Transparent Pricing
        </h2>
        <p className="text-muted-foreground">
          Join our Founding Exporters program
        </p>
      </div>
      
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-2xl p-8 border-2 border-primary shadow-lg">
          <div className="text-center">
            <span className="inline-block bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
              FOUNDING EXPORTER OFFER
            </span>
            <h3 className="font-heading text-2xl font-bold mb-2">Pro Version FREE</h3>
            <p className="text-sm text-muted-foreground mb-4">for 12 months</p>
            <div className="flex items-baseline justify-center gap-1 mb-6">
              <span className="text-lg text-muted-foreground line-through">$36/month</span>
              <span className="text-4xl font-bold text-green-600 ml-2">$0</span>
            </div>
            <p className="text-sm font-medium text-amber-600 mb-6">
              First 500 signups only
            </p>
            
            <ul className="space-y-3 text-left mb-8">
              {[
                "Unlimited product listings",
                "AI-powered auto-responses",
                "Lead management pipeline",
                "AI Smart Match for buyers",
                "Basic analytics dashboard",
                "Email support"
              ].map((feature, i) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            
            <Button className="w-full h-12" onClick={() => window.location.href = '/auth'}>
              Claim Your Free Spot
            </Button>
            
            <p className="text-xs text-muted-foreground mt-4">
              No credit card required. Limited spots available.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// AI Demo Slideshow
const DemoSlideshow = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  const slides = [
    {
      title: "AI Smart Match",
      description: "Buyers describe what they need — AI matches them with relevant verified sellers",
      icon: Target,
      bg: "from-amber-500 to-orange-600"
    },
    {
      title: "Auto-Response Drafts",
      description: "AI drafts professional responses to every inquiry. You review and approve before sending.",
      icon: Bot,
      bg: "from-blue-500 to-indigo-600"
    },
    {
      title: "Follow-up Sequences",
      description: "AI creates follow-up drafts to keep leads warm. You stay in control of every message.",
      icon: Clock,
      bg: "from-green-500 to-emerald-600"
    },
    {
      title: "Lead Pipeline",
      description: "Track every lead from first contact to closed deal in a simple 5-stage pipeline.",
      icon: TrendingUp,
      bg: "from-purple-500 to-violet-600"
    },
    {
      title: "Verified Sellers",
      description: "Business verification builds trust. Buyers know they're dealing with legitimate exporters.",
      icon: BadgeCheck,
      bg: "from-teal-500 to-cyan-600"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  return (
    <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-slate-900">
      {/* Aspect ratio that works better on mobile */}
      <div className="aspect-[4/3] sm:aspect-video relative">
        {slides.map((slide, index) => {
          const Icon = slide.icon;
          return (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <div className={`w-full h-full bg-gradient-to-br ${slide.bg} flex items-center justify-center p-4 sm:p-8 md:p-12`}>
                <div className="text-center text-white max-w-2xl">
                  <div className="inline-flex items-center justify-center w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-white/20 backdrop-blur-sm mb-4 sm:mb-6">
                    <Icon className="h-7 w-7 sm:h-10 sm:w-10" />
                  </div>
                  <h3 className="text-xl sm:text-2xl md:text-4xl font-bold mb-2 sm:mb-4">{slide.title}</h3>
                  <p className="text-sm sm:text-lg md:text-xl text-white/90 px-2">{slide.description}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={prevSlide}
        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 flex items-center justify-center text-white z-20"
      >
        <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 flex items-center justify-center text-white z-20"
      >
        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
      </button>

      <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentSlide ? 'bg-white scale-125' : 'bg-white/50'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// Stats Counter - Value Proposition
const StatsSection = () => {
  const stats = [
    { value: "Free", label: "For 12 Months", icon: Sparkles },
    { value: "0%", label: "Commission", icon: DollarSign },
    { value: "Verified", label: "Leads Only", icon: BadgeCheck },
    { value: "Zero", label: "Middlemen", icon: Users }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 max-w-4xl mx-auto px-2">
      {stats.map((stat, i) => {
        const Icon = stat.icon;
        return (
          <div key={i} className="text-center p-3 sm:p-6 rounded-xl bg-white/10 backdrop-blur-sm">
            <Icon className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-2 sm:mb-3 text-accent" />
            <p className="text-xl sm:text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</p>
            <p className="text-xs sm:text-sm text-slate-300">{stat.label}</p>
          </div>
        );
      })}
    </div>
  );
};

// Security Section - Without false claims
const SecuritySection = () => (
  <section className="py-12 sm:py-16 bg-slate-900 text-white" data-testid="security-section">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-8 sm:mb-12">
        <Shield className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 text-green-400" />
        <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Your Data is Safe With Us</h2>
        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base px-4">
          We understand exporters' concerns about data security. Your buyer data stays protected.
        </p>
      </div>
      
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <div className="bg-slate-800 rounded-xl p-4 sm:p-6 text-center">
          <Lock className="h-8 w-8 sm:h-10 sm:w-10 mx-auto mb-2 sm:mb-4 text-green-400" />
          <h3 className="font-semibold mb-1 sm:mb-2 text-sm sm:text-base">256-bit SSL</h3>
          <p className="text-xs sm:text-sm text-slate-400">Bank-grade encryption</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-4 sm:p-6 text-center">
          <Globe className="h-8 w-8 sm:h-10 sm:w-10 mx-auto mb-2 sm:mb-4 text-blue-400" />
          <h3 className="font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Global Servers</h3>
          <p className="text-xs sm:text-sm text-slate-400">Data hosted securely worldwide</p>
        </div>
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-4 sm:p-6 text-center border-2 border-blue-400">
          <div className="relative">
            <BadgeCheck className="h-8 w-8 sm:h-10 sm:w-10 mx-auto mb-2 sm:mb-4 text-white" />
            <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">NEW</span>
          </div>
          <h3 className="font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Video ID Verification</h3>
          <p className="text-xs sm:text-sm text-blue-100">AI-powered face verification</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-4 sm:p-6 text-center">
          <Shield className="h-8 w-8 sm:h-10 sm:w-10 mx-auto mb-2 sm:mb-4 text-purple-400" />
          <h3 className="font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Privacy First</h3>
          <p className="text-xs sm:text-sm text-slate-400">No third-party sharing</p>
        </div>
      </div>
      
      {/* Video Verification Highlight */}
      <div className="mt-8 sm:mt-12 bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-2xl p-6 sm:p-8 border border-blue-500/30">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="flex-shrink-0">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
              <BadgeCheck className="h-10 w-10 sm:h-12 sm:w-12 text-white" />
            </div>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-xl sm:text-2xl font-bold mb-2">Video Verified Sellers</h3>
            <p className="text-slate-300 text-sm sm:text-base mb-4">
              Every seller on GlobalVyapar completes a 6-step video verification process with AI-powered face detection and liveness checks. 
              This ensures you're dealing with real, verified business owners - not scammers.
            </p>
            <div className="flex flex-wrap gap-2 justify-center md:justify-start">
              <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-xs sm:text-sm">Face Detection</span>
              <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs sm:text-sm">Liveness Check</span>
              <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs sm:text-sm">Identity Verification</span>
              <span className="px-3 py-1 bg-amber-500/20 text-amber-300 rounded-full text-xs sm:text-sm">Admin Review</span>
            </div>
          </div>
        </div>
      </div>
      
      <p className="text-center text-xs sm:text-sm text-slate-500 mt-6 sm:mt-8">
        Compliant with international data protection standards
      </p>
    </div>
  </section>
);

// Who Is This For Section
const WhoIsThisFor = () => {
  const navigate = useNavigate();
  
  return (
  <section className="py-12 sm:py-20 bg-gradient-to-br from-slate-50 to-white" data-testid="who-is-this-for">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-8 sm:mb-12">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary mb-3 sm:mb-4">
          Built for Global Exporters & Buyers
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base px-4">
          Whether you&apos;re exporting electronics from Germany or sourcing textiles for the US, GlobalVyapar connects you.
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4 sm:gap-8">
        {/* For Sellers */}
        <div className="bg-white rounded-2xl p-4 sm:p-8 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <div className="p-2 sm:p-3 bg-green-100 rounded-xl">
              <Package className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
            </div>
            <div>
              <h3 className="font-heading text-lg sm:text-xl font-bold">For Exporters (Sellers)</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">Verified manufacturers & traders</p>
            </div>
          </div>
          <ul className="space-y-2 sm:space-y-4">
            {[
              "List your products and get discovered by global buyers",
              "AI drafts responses to every inquiry (you approve before sending)",
              "Automated follow-up draft sequences",
              "Video ID verification builds buyer trust",
              "Track all leads in one pipeline"
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2 sm:gap-3">
                <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700 text-sm sm:text-base">{item}</span>
              </li>
            ))}
          </ul>
          <Button 
            onClick={() => navigate('/auth')} 
            className="w-full mt-6 bg-green-600 hover:bg-green-700"
          >
            Register as Exporter
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
        
        {/* For Buyers */}
        <div className="bg-white rounded-2xl p-4 sm:p-8 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <div className="p-2 sm:p-3 bg-blue-100 rounded-xl">
              <Users className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
            </div>
            <div>
              <h3 className="font-heading text-lg sm:text-xl font-bold">For Importers (Buyers)</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">Global traders & businesses</p>
            </div>
          </div>
          <ul className="space-y-2 sm:space-y-4">
            {[
              "AI Smart Match finds relevant suppliers instantly",
              "Browse verified exporters worldwide",
              "Get reasons why each supplier matches your needs",
              "Receive fast, professional responses",
              "Connect with verified sellers worldwide"
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2 sm:gap-3">
                <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700 text-sm sm:text-base">{item}</span>
              </li>
            ))}
          </ul>
          <Button 
            onClick={() => navigate('/buyer-signup')} 
            className="w-full mt-6 bg-blue-600 hover:bg-blue-700"
          >
            <Globe className="h-4 w-4 mr-2" />
            Get Verified as International Buyer
          </Button>
        </div>
      </div>
    </div>
  </section>
);};

// Footer with proper columns
const Footer = () => {
  const navigate = useNavigate();
  
  return (
    <footer className="bg-slate-900 text-white py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
          {/* Column 1 - Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-3 sm:mb-4">
              <img src="/logo-cropped.png" alt="GlobalVyapar" className="h-8 sm:h-10 w-auto" />
            </div>
            <p className="text-xs sm:text-sm text-slate-400 mb-2 sm:mb-3">
              AI-powered B2B trade platform for global exporters.
            </p>
            <p className="text-xs text-slate-500">Est. 2026</p>
          </div>
          
          {/* Column 2 - Platform */}
          <div>
            <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Platform</h4>
            <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-slate-400">
              <li><a href="/marketplace" className="hover:text-white transition-colors">Marketplace</a></li>
              <li><a href="/auth" className="hover:text-white transition-colors">Login / Register</a></li>
              <li><a href="/support" className="hover:text-white transition-colors">Support</a></li>
            </ul>
          </div>
          
          {/* Column 3 - Security */}
          <div>
            <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Security</h4>
            <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-slate-400">
              <li className="flex items-center gap-1.5">
                <Lock className="h-3 w-3 flex-shrink-0" />
                <span>256-bit SSL</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Globe className="h-3 w-3 flex-shrink-0" />
                <span>Secure Data</span>
              </li>
              <li className="flex items-center gap-1.5">
                <BadgeCheck className="h-3 w-3 flex-shrink-0" />
                <span>Verified Business</span>
              </li>
            </ul>
          </div>
          
          {/* Column 4 - Contact */}
          <div>
            <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Contact</h4>
            <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-slate-400">
              <li>
                <a href="mailto:support@globalvyapar.co.in" className="hover:text-white transition-colors text-xs sm:text-sm break-all">
                  support@globalvyapar.co.in
                </a>
              </li>
              <li className="text-slate-500">Global Operations</li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-slate-800 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
          <p className="text-slate-400 text-xs sm:text-sm">
            © 2026 GlobalVyapar. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs sm:text-sm text-slate-400">
            <a href="/privacy-policy" className="hover:text-white transition-colors">Privacy</a>
            <a href="/terms-of-service" className="hover:text-white transition-colors">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const [foundingCount, setFoundingCount] = useState({ claimed: 0, total_slots: 500, total_exporters: 0 });

  useEffect(() => {
    // Fetch founding count for banner
    const fetchFoundingCount = async () => {
      try {
        const res = await axios.get(`${API}/api/pricing/founding/count`);
        setFoundingCount(res.data);
      } catch (error) {
        // Silent fail - use defaults
      }
    };
    fetchFoundingCount();
  }, []);

  const progressPercentage = (foundingCount.claimed / foundingCount.total_slots) * 100;

  return (
    <div className="min-h-screen">
      <SEO {...SEO_CONFIGS.home} />
      
      {/* Founding Exporter Banner */}
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white py-2.5 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 text-center">
          <Rocket className="h-4 w-4 animate-pulse" />
          <span className="text-sm font-medium">
            First 500 exporters get Pro FREE for 12 months
          </span>
          <span className="hidden sm:inline text-white/70">•</span>
          <span className="font-bold">
            {foundingCount.claimed}/{foundingCount.total_slots} claimed
          </span>
          <div className="w-24 h-1.5 bg-white/30 rounded-full overflow-hidden ml-1">
            <div 
              className="h-full bg-white transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="ml-2 h-7 px-3 text-xs bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={() => navigate('/pricing')}
          >
            Claim Spot →
          </Button>
        </div>
      </div>

      {/* Trust Bar - Without false claims */}
      <div className="bg-slate-900 py-3">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            <div className="flex items-center gap-2 text-white">
              <Lock className="h-4 w-4 text-green-400" />
              <span className="text-xs font-medium">256-bit SSL</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <Shield className="h-4 w-4 text-blue-400" />
              <span className="text-xs font-medium">Secure Data Centers</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="h-4 w-4 text-amber-400" />
              <span className="text-xs font-medium">Free During Beta</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="border-b border-border bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2" data-testid="logo">
              <img src="/logo-cropped.png" alt="GlobalVyapar" className="h-10 w-auto" />
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="hidden md:flex"
                data-testid="nav-how-it-works"
              >
                How It Works
              </Button>
              <Button 
                variant="ghost"
                onClick={() => document.getElementById('ai-features')?.scrollIntoView({ behavior: 'smooth' })}
                className="hidden md:flex"
                data-testid="nav-ai-features"
              >
                AI Features
              </Button>
              <Button 
                variant="ghost"
                onClick={() => document.getElementById('blog')?.scrollIntoView({ behavior: 'smooth' })}
                className="hidden md:flex"
                data-testid="nav-blog"
              >
                Blog
              </Button>
              <Button 
                variant="ghost"
                onClick={() => navigate('/pricing')}
                className="hidden md:flex"
                data-testid="nav-pricing"
              >
                Pricing
              </Button>
              <Button 
                variant="ghost"
                onClick={() => navigate('/marketplace')}
                className="hidden sm:flex"
              >
                Browse Products
              </Button>
              <LanguageSelector variant="ghost" />
              <Button 
                onClick={() => navigate('/auth')} 
                variant="default"
                data-testid="nav-login-register-btn"
              >
                Login / Register
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section relative py-20 md:py-28">
        <div className="hero-pattern"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-red-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6 animate-pulse">
              <Rocket className="h-4 w-4 text-red-400" />
              <span className="text-white/90 text-sm font-medium">Only {500 - foundingCount.claimed} spots left — Claim yours now!</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-4 sm:mb-6 px-2" data-testid="hero-heading">
              B2B Export Made
              <br />
              <span className="text-accent">Stupidly Simple</span>
            </h1>
            <p className="text-base sm:text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-3 sm:mb-4 px-4">
              No trade shows. No cold calls. Just verified leads.
            </p>
            <p className="text-sm sm:text-lg text-slate-400 mb-6 sm:mb-10">
              <span className="text-green-400 font-semibold">First 500 exporters</span> get Pro FREE for 12 months. After that, $36/month.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-8 sm:mb-12 px-4">
              <Button 
                size="lg" 
                onClick={() => navigate('/auth')} 
                className="bg-accent hover:bg-accent/90 text-primary font-bold h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg w-full sm:w-auto"
                data-testid="hero-login-register-btn"
              >
                Claim Your Free Spot Now
                <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                onClick={() => navigate('/marketplace')} 
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 font-semibold h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg w-full sm:w-auto"
                data-testid="hero-browse-btn"
              >
                <Package className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Browse Products First
              </Button>
            </div>
            
            {/* Honest Stats */}
            <StatsSection />
          </div>
        </div>
      </section>

      {/* Who Is This For */}
      <WhoIsThisFor />

      {/* How It Works - Detailed Section */}
      <section id="how-it-works" className="py-16 sm:py-24 bg-white" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-100">
              <Sparkles className="h-3 w-3 mr-1" />
              AI-Powered Features
            </Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-primary mb-4">
              How GlobalVyapar Works
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From buyer inquiry to closed deal — see how our AI handles everything
            </p>
          </div>

          {/* Process Steps */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            {[
              { step: '1', title: 'Buyer Inquires', desc: 'A global buyer finds your products and sends an inquiry', icon: MessageSquare, color: 'bg-blue-100 text-blue-600' },
              { step: '2', title: 'AI Responds', desc: 'Within 5 minutes, AI sends a professional, personalized reply', icon: Bot, color: 'bg-teal-100 text-teal-600' },
              { step: '3', title: 'AI Negotiates', desc: 'AI handles price discussions within your set boundaries', icon: DollarSign, color: 'bg-amber-100 text-amber-600' },
              { step: '4', title: 'You Close', desc: 'Review the qualified lead and finalize the deal', icon: CheckCircle, color: 'bg-green-100 text-green-600' }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="relative">
                  <Card className="text-center p-6 h-full hover:shadow-lg transition-shadow">
                    <div className={`w-12 h-12 ${item.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="absolute -top-3 -left-3 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold text-sm">
                      {item.step}
                    </div>
                    <h3 className="font-semibold mb-2">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </Card>
                  {i < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                      <ArrowRight className="h-6 w-6 text-slate-300" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Feature Details */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Bot, title: 'AI Auto-Reply', subtitle: 'Respond in under 5 minutes', description: 'When a buyer sends an inquiry, our AI immediately crafts a professional, personalized response. No more missed opportunities.', color: 'from-blue-500 to-cyan-500' },
              { icon: DollarSign, title: 'AI Price Negotiation', subtitle: 'Set your floor price, AI handles the rest', description: 'You define your minimum acceptable price. The AI negotiates professionally, never dropping below your floor.', color: 'from-emerald-500 to-teal-500' },
              { icon: Target, title: 'Strategy Bands', subtitle: 'Choose your communication style', description: 'Select from Professional, Friendly, or Urgent tones. The AI adapts its messaging to match your brand.', color: 'from-purple-500 to-pink-500' },
              { icon: FileText, title: 'AI Draft Generation', subtitle: 'Professional messages in seconds', description: 'Generate follow-up emails, quotations, and responses with one click. Review and customize before sending.', color: 'from-orange-500 to-amber-500' },
              { icon: Shield, title: 'Buyer Verification', subtitle: 'Only deal with legitimate businesses', description: 'Every buyer is verified through video ID and business registration. Spend your time on real opportunities.', color: 'from-green-500 to-emerald-500' },
              { icon: RefreshCw, title: '30-Day Auto Follow-up', subtitle: 'Never let a lead go cold', description: 'AI automatically follows up with inactive leads for 30 days. Track every conversation in your pipeline.', color: 'from-indigo-500 to-violet-500' }
            ].map((feature, i) => {
              const Icon = feature.icon;
              return (
                <Card key={i} className="group hover:shadow-lg transition-all overflow-hidden">
                  <CardHeader>
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="h-7 w-7" />
                    </div>
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                    <CardDescription>{feature.subtitle}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* AI Example */}
          <div className="mt-16 bg-slate-900 rounded-2xl p-6 sm:p-8 text-white">
            <h3 className="text-xl font-bold mb-6 text-center">See AI Negotiation in Action</h3>
            <div className="max-w-3xl mx-auto space-y-4">
              <div className="flex justify-start">
                <div className="bg-slate-700 rounded-lg p-4 max-w-[80%]">
                  <p className="text-sm text-slate-300 mb-1">Buyer:</p>
                  <p className="text-slate-100">"Your price is too high. We can get cumin at USD 2,200/MT from another supplier."</p>
                </div>
              </div>
              <div className="flex justify-end">
                <div className="bg-teal-600 rounded-lg p-4 max-w-[80%]">
                  <p className="text-sm text-teal-200 mb-1">AI Response:</p>
                  <p className="text-white">"Thank you for sharing that. While we understand price is important, our cumin seeds offer 99% purity with consistent quality. For a valued partner like yourself, we can offer USD 2,400/MT for orders above 5 MT."</p>
                </div>
              </div>
              <div className="text-center">
                <Badge className="bg-emerald-500/20 text-emerald-300 mt-4">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  AI never went below the floor price of USD 2,300/MT
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Demo Section - Real AI Features */}
      <section id="ai-features" className="py-16 sm:py-24 bg-slate-50" data-testid="demo-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-100">
              <Sparkles className="h-3 w-3 mr-1" />
              AI Features Preview
            </Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-primary mb-4">
              See Our AI in Action
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Real screenshots of the AI-powered features that help you close more deals
            </p>
          </div>

          {/* Screenshot Carousel */}
          <div className="relative">
            <Tabs defaultValue="strategy" className="w-full">
              <TabsList className="grid grid-cols-4 max-w-2xl mx-auto mb-8">
                <TabsTrigger value="strategy" className="text-xs sm:text-sm">Strategy Bands</TabsTrigger>
                <TabsTrigger value="drafts" className="text-xs sm:text-sm">AI Drafts</TabsTrigger>
                <TabsTrigger value="negotiate" className="text-xs sm:text-sm">AI Negotiation</TabsTrigger>
                <TabsTrigger value="followup" className="text-xs sm:text-sm">Auto Follow-up</TabsTrigger>
              </TabsList>

              {/* Strategy Bands Tab */}
              <TabsContent value="strategy" className="mt-0">
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-3 sm:p-4 shadow-2xl max-w-4xl mx-auto">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <span className="text-slate-400 text-xs ml-2">Inquiries → Strategy Bands</span>
                  </div>
                  <div className="rounded-lg overflow-hidden bg-white p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Shield className="h-5 w-5 text-purple-600" />
                      <h4 className="font-semibold">Select Your Negotiation Strategy</h4>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">Choose a strategy based on your priorities. Each includes a ready-to-send message.</p>
                    
                    <div className="space-y-3">
                      {/* Conservative Strategy */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm flex items-center gap-2">
                            <Target className="h-4 w-4 text-blue-600" />
                            Conservative
                          </span>
                          <Badge variant="outline" className="text-xs">Protect Margin</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                          <div><span className="text-muted-foreground">Suggested:</span> <span className="font-medium">$2,450/MT</span></div>
                          <div><span className="text-muted-foreground">Margin:</span> <span className="font-medium text-green-600">+12%</span></div>
                        </div>
                        <p className="text-xs text-muted-foreground italic mb-2">💡 Maintains premium positioning while showing flexibility</p>
                        <div className="flex gap-2">
                          <Button size="sm" className="gap-1 text-xs"><CheckCircle className="h-3 w-3" /> Send This</Button>
                          <Button size="sm" variant="outline" className="text-xs">Edit First</Button>
                        </div>
                      </div>

                      {/* Competitive Strategy */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-green-600" />
                            Competitive
                          </span>
                          <Badge variant="outline" className="text-xs">Win Deal</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                          <div><span className="text-muted-foreground">Suggested:</span> <span className="font-medium">$2,350/MT</span></div>
                          <div><span className="text-muted-foreground">Margin:</span> <span className="font-medium text-amber-600">+8%</span></div>
                        </div>
                        <p className="text-xs text-muted-foreground italic mb-2">💡 Matches market rate to secure long-term relationship</p>
                        <div className="flex gap-2">
                          <Button size="sm" className="gap-1 text-xs"><CheckCircle className="h-3 w-3" /> Send This</Button>
                          <Button size="sm" variant="outline" className="text-xs">Edit First</Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-center mt-4 text-muted-foreground text-sm">
                  AI generates multiple negotiation strategies with pre-written messages
                </p>
              </TabsContent>

              {/* AI Drafts Tab */}
              <TabsContent value="drafts" className="mt-0">
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-3 sm:p-4 shadow-2xl max-w-4xl mx-auto">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <span className="text-slate-400 text-xs ml-2">Inquiries → AI Draft Response</span>
                  </div>
                  <div className="rounded-lg overflow-hidden bg-white p-4">
                    {/* Buyer Message */}
                    <div className="bg-slate-100 rounded-lg p-3 mb-4">
                      <p className="text-xs text-slate-500 mb-1">Buyer Inquiry from Dubai:</p>
                      <p className="text-sm">"We need 5 MT of cumin seeds. What's your best price and delivery time to UAE?"</p>
                    </div>
                    
                    {/* AI Draft */}
                    <div className="border-l-4 border-teal-500 bg-teal-50 rounded-r-lg p-4 mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Bot className="h-4 w-4 text-teal-600" />
                        <span className="text-sm font-medium text-teal-700">AI Draft Response</span>
                        <Badge className="bg-teal-100 text-teal-700 text-xs">Professional Tone</Badge>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">
                        "Dear Buyer,<br/><br/>
                        Thank you for your inquiry regarding cumin seeds. For 5 MT, we can offer USD 2,400/MT CIF Dubai with the following specifications:<br/><br/>
                        • 99% purity, Singapore quality<br/>
                        • Moisture content: 8% max<br/>
                        • Delivery: 15-20 days after LC confirmation<br/><br/>
                        All shipments include quality certification and phytosanitary documents. Would you like us to send a detailed quotation?<br/><br/>
                        Best regards"
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <Button className="gap-2 bg-green-600 hover:bg-green-700">
                        <CheckCircle className="h-4 w-4" />
                        Approve & Send
                      </Button>
                      <Button variant="outline" className="gap-2">
                        <MessageSquare className="h-4 w-4" />
                        Edit First
                      </Button>
                      <Button variant="ghost" className="gap-2">
                        <RefreshCw className="h-4 w-4" />
                        Regenerate
                      </Button>
                    </div>
                  </div>
                </div>
                <p className="text-center mt-4 text-muted-foreground text-sm">
                  AI drafts professional responses based on your product details and conversation history
                </p>
              </TabsContent>

              {/* AI Negotiation Tab */}
              <TabsContent value="negotiate" className="mt-0">
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-3 sm:p-4 shadow-2xl max-w-4xl mx-auto">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <span className="text-slate-400 text-xs ml-2">Live Negotiation Assist</span>
                  </div>
                  <div className="rounded-lg overflow-hidden bg-white p-4">
                    {/* Price Settings */}
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-purple-600" />
                        Your Price Boundaries
                      </h4>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="bg-white rounded-lg p-3 border">
                          <p className="text-xs text-slate-500">Floor Price</p>
                          <p className="text-lg font-bold text-red-600">$2,300/MT</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border">
                          <p className="text-xs text-slate-500">Target Price</p>
                          <p className="text-lg font-bold text-amber-600">$2,450/MT</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border">
                          <p className="text-xs text-slate-500">List Price</p>
                          <p className="text-lg font-bold text-green-600">$2,600/MT</p>
                        </div>
                      </div>
                    </div>

                    {/* Conversation */}
                    <div className="space-y-3 mb-4">
                      <div className="flex justify-start">
                        <div className="bg-slate-100 rounded-lg p-3 max-w-[80%]">
                          <p className="text-xs text-slate-500 mb-1">Buyer:</p>
                          <p className="text-sm">"$2,600 is too high. We can get it at $2,200 elsewhere."</p>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <div className="bg-teal-100 rounded-lg p-3 max-w-[80%] border-l-4 border-teal-500">
                          <p className="text-xs text-teal-600 mb-1">AI Counter-Offer:</p>
                          <p className="text-sm">"We understand price is important. Given your volume of 5 MT, we can offer $2,400/MT — which includes quality certification and priority shipping."</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium text-green-700">AI stayed above your floor price of $2,300/MT</span>
                    </div>
                  </div>
                </div>
                <p className="text-center mt-4 text-muted-foreground text-sm">
                  Set your price boundaries — AI negotiates professionally, never going below your floor
                </p>
              </TabsContent>

              {/* Auto Follow-up Tab */}
              <TabsContent value="followup" className="mt-0">
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-3 sm:p-4 shadow-2xl max-w-4xl mx-auto">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <span className="text-slate-400 text-xs ml-2">Lead Pipeline → Auto Follow-up</span>
                  </div>
                  <div className="rounded-lg overflow-hidden bg-white p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Clock className="h-5 w-5 text-blue-600" />
                        30-Day Follow-up Sequence
                      </h4>
                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                    </div>

                    {/* Timeline */}
                    <div className="space-y-3">
                      {[
                        { day: 'Day 1', status: 'sent', message: 'Initial response with pricing sent', time: '2 hours ago' },
                        { day: 'Day 3', status: 'scheduled', message: 'Follow-up: Check if quotation received', time: 'In 2 days' },
                        { day: 'Day 7', status: 'scheduled', message: 'Follow-up: Any questions about specs?', time: 'In 6 days' },
                        { day: 'Day 14', status: 'pending', message: 'Follow-up: Special offer if order this month', time: 'In 13 days' },
                        { day: 'Day 30', status: 'pending', message: 'Final follow-up: Keep in touch', time: 'In 29 days' }
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-slate-50">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-medium ${
                            item.status === 'sent' ? 'bg-green-100 text-green-700' :
                            item.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-200 text-slate-500'
                          }`}>
                            {item.day.split(' ')[1]}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{item.message}</p>
                            <p className="text-xs text-muted-foreground">{item.time}</p>
                          </div>
                          <Badge variant="outline" className={`text-xs ${
                            item.status === 'sent' ? 'border-green-500 text-green-600' :
                            item.status === 'scheduled' ? 'border-blue-500 text-blue-600' :
                            'border-slate-300'
                          }`}>
                            {item.status === 'sent' ? '✓ Sent' : item.status === 'scheduled' ? 'Scheduled' : 'Pending'}
                          </Badge>
                        </div>
                      ))}
                    </div>

                    <p className="text-xs text-center text-muted-foreground mt-4 p-2 bg-amber-50 rounded">
                      ⚡ AI generates personalized follow-ups. You review before each is sent.
                    </p>
                  </div>
                </div>
                <p className="text-center mt-4 text-muted-foreground text-sm">
                  Automated follow-up sequences keep leads warm for 30 days
                </p>
              </TabsContent>
            </Tabs>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <Button onClick={() => navigate('/auth')} size="lg" className="gap-2">
              Try These Features Free <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section id="blog" className="py-16 sm:py-24 bg-slate-50" data-testid="blog-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100">
              <BookOpen className="h-3 w-3 mr-1" />
              Knowledge Hub
            </Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-primary mb-4">
              Export Tips & Insights
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Expert advice to help you grow your international trade business
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Blog Post 1 */}
            <Card className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-48 bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center">
                <Globe className="h-16 w-16 text-white/50" />
              </div>
              <CardHeader>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <Badge variant="secondary">Export Tips</Badge>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Recent
                  </span>
                </div>
                <CardTitle className="line-clamp-2">How Exporters Can Find Global Buyers in 2026</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                  Discover proven strategies for exporters to connect with international buyers in the digital age. Learn how to leverage B2B marketplaces, build your digital presence, and use AI to scale your outreach.
                </p>
                <Link to="/blog/how-exporters-find-global-buyers-2026" className="text-primary font-medium text-sm hover:underline flex items-center gap-1">
                  Read More <ArrowRight className="h-4 w-4" />
                </Link>
              </CardContent>
            </Card>

            {/* Blog Post 2 */}
            <Card className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-48 bg-gradient-to-br from-blue-500 to-indigo-700 flex items-center justify-center">
                <Bot className="h-16 w-16 text-white/50" />
              </div>
              <CardHeader>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <Badge variant="secondary">Global Markets</Badge>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Recent
                  </span>
                </div>
                <CardTitle className="line-clamp-2">Why AI is Changing B2B Export Trade</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                  Artificial intelligence is revolutionizing how exporters find buyers, negotiate deals, and manage relationships. Learn how AI solves traditional export challenges like slow response times and manual follow-ups.
                </p>
                <Link to="/blog/why-ai-changing-b2b-export-trade" className="text-primary font-medium text-sm hover:underline flex items-center gap-1">
                  Read More <ArrowRight className="h-4 w-4" />
                </Link>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <Button variant="outline" onClick={() => navigate('/blog')} className="gap-2">
              View All Articles <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <SecuritySection />

      {/* Pricing Section */}
      <PricingSection />

      {/* Waitlist / CTA Section */}
      <WaitlistSection />

      {/* CTA Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-primary to-blue-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
              Ready to Try AI-Powered Lead Management?
            </h2>
            <p className="text-base sm:text-xl text-slate-300 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
              Join our early access program. Free during beta.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <Button 
                size="lg" 
                onClick={() => navigate('/auth')} 
                className="bg-accent hover:bg-accent/90 text-primary font-bold h-14 px-10 text-lg"
                data-testid="cta-login-register-btn"
              >
                Get Started Free
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/support')} 
                className="border-white text-white hover:bg-white/10 h-14 px-10 text-lg"
              >
                <HelpCircle className="h-5 w-5 mr-2" />
                Questions? Get Help
              </Button>
            </div>
            <p className="text-sm text-slate-400 mt-6">
              No credit card required. Free during beta.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
